import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Plus } from 'lucide-react';
import crm, { STAGES } from '../crm/api';
import LeadDetail from '../crm/LeadDetail';
import LeadCaptureModal from '../crm/LeadCaptureModal';
import LeadQuickAdd from '../crm/LeadQuickAdd';
import LeadGrid from '../crm/LeadGrid';
import LeadLedger from '../crm/LeadLedger';
import ChatPanel from '../crm/ChatPanel';
import { getLocalLeads, syncNow } from '../offline/leads';

export default function MyLeads() {
  const nav = useNavigate();
  const [name, setName] = useState('Employee');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [confFilter, setConfFilter] = useState('all');   // conference (campaign) filter
  const [openId, setOpenId] = useState(null);
  const [captureLead, setCaptureLead] = useState(null); // lead being enriched via the quick popup
  const [showAdd, setShowAdd] = useState(false);        // manual "Add inquiry" modal
  const [view, setView] = useState(
    ['grid', 'list'].includes(new URLSearchParams(window.location.search).get('view'))
      ? new URLSearchParams(window.location.search).get('view')
      : 'list'
  );

  useEffect(() => {
    if (!localStorage.getItem('employee_id')) { nav('/employee/login'); return; }
    setName(localStorage.getItem('employee_name') || 'Employee');
    load();
  }, [nav]);

  // Offline-first: show the local mirror at once, then reconcile with the server
  // and re-read. With no signal the mirror is all there is, and that is fine —
  // the page still works. The old direct /leads call is the fallback for a first
  // run where the mirror is empty but we are online.
  const load = async () => {
    setLoading(true);
    try {
      const local = await getLocalLeads();
      if (local.length) { setLeads(local); setLoading(false); }

      await syncNow();
      const fresh = await getLocalLeads();

      if (fresh.length) {
        setLeads(fresh);
      } else {
        // Mirror still empty (brand-new sync store) — fall back to a direct read.
        const { data } = await crm.get('/leads', { params: { limit: 100 } });
        setLeads(data.leads || []);
      }
    } catch {
      if (!leads.length) setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    ['employee_token', 'employee_id', 'employee_name', 'employee_slug', 'crm_token', 'crm_user'].forEach((k) => localStorage.removeItem(k));
    nav('/employee/login');
  };

  const count = (s) => leads.filter((l) => l.status === s).length;
  const kpis = [
    { label: 'Total', value: leads.length, color: '#0b6e3e' },
    { label: 'New', value: count('new'), color: '#0e7490' },
    { label: 'Contacted', value: count('contacted'), color: '#7c3aed' },
    { label: 'Won', value: count('won'), color: '#1a8046' },
  ];

  // Unique conferences present in this rep's leads — powers the filter dropdown.
  const confOf = (l) => (l.campaign_id != null ? String(l.campaign_id) : (l.Campaign?.id != null ? String(l.Campaign.id) : ''));
  const conferences = Array.from(
    leads.reduce((m, l) => { const id = confOf(l); if (id) m.set(id, l.Campaign?.name || `Conference ${id}`); return m; }, new Map())
  ).map(([id, name]) => ({ id, name }));

  const filtered = leads.filter((l) => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (confFilter !== 'all') {
      const cid = confOf(l);
      if (confFilter === 'none' ? cid !== '' : cid !== confFilter) return false;
    }
    if (!q) return true;
    const s = q.toLowerCase();
    return [l.name, l.company, l.email, l.phone, l.interest].some((v) => (v || '').toLowerCase().includes(s));
  });

  // Bare 10-digit numbers get India's 91; anything longer already carries its own
  // country code (e.g. a Dubai/UK lead), so don't prepend a wrong one.
  const wa = (phone) => { const p = (phone || '').replace(/[\s\-()+]/g, ''); const num = p.length === 10 ? '91' + p : p; window.open(`https://wa.me/${num}`, '_blank'); };

  // Export the current view to CSV. Sent through the authed CRM client (Bearer
  // token) as a blob, so a plain <a href> that would drop the auth header isn't used.
  const exportCsv = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (confFilter !== 'all') params.campaign = confFilter;
      const res = await crm.get('/leads/export.xlsx', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `DFC-Leads-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { alert('Could not export leads. Please try again.'); }
  };

  return (
    <div className="pt-1">
      <div className="max-w-[1180px] space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {kpis.map((k) => (
            <div key={k.label} className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-4 shadow-sm">
              <div className="text-[13px] text-[var(--muted)]">{k.label}</div>
              <div className="text-2xl font-extrabold mt-1" style={{ color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* view toggle */}
        <div className="inline-flex rounded-xl p-1" style={{ background: 'var(--soft)' }}>
          {['list', 'grid'].map((v) => (
            <button key={v} onClick={() => setView(v)} className="px-5 py-1.5 rounded-lg text-[13px] font-bold capitalize transition-colors"
              style={view === v ? { background: 'var(--card)', color: 'var(--acc2)', boxShadow: '0 1px 3px rgba(0,0,0,.12)' } : { color: 'var(--muted)' }}>{v}</button>
          ))}
        </div>

        <>
        {/* search + filter chips */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" size={18} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…"
              className="w-full pl-10 pr-4 py-2.5 bg-[var(--card)] border border-[var(--border)] rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[{ key: 'all', label: 'All' }, ...STAGES].map((s) => (
              <button key={s.key} onClick={() => setStatusFilter(s.key)}
                className={`px-3 py-2 rounded-lg text-[13px] font-semibold border transition-colors ${statusFilter === s.key ? 'text-white border-transparent' : 'bg-[var(--card)] text-[var(--muted)] border-[var(--border)] hover:bg-[var(--soft)]'}`}
                style={statusFilter === s.key ? { background: s.key === 'all' ? '#0b6e3e' : s.color } : {}}>
                {s.label}
              </button>
            ))}
          </div>
          {conferences.length > 0 && (
            <select value={confFilter} onChange={(e) => setConfFilter(e.target.value)} title="Filter by conference"
              className="px-3 py-2 rounded-lg text-[13px] font-semibold border border-[var(--border)] bg-[var(--card)] text-[var(--text)]">
              <option value="all">All conferences</option>
              <option value="none">Direct (no conference)</option>
              {conferences.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <div className="flex gap-1.5 ml-auto">
            <button onClick={exportCsv} title="Export to Excel"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold border border-[var(--border)] bg-[var(--card)] text-[var(--muted)] hover:bg-[var(--soft)]">
              <Download size={15} /> Export
            </button>
            <button onClick={() => setShowAdd(true)} title="Add an inquiry manually"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-bold text-white border border-transparent"
              style={{ background: '#0b6e3e' }}>
              <Plus size={15} /> Add Inquiry
            </button>
          </div>
        </div>

        {/* leads */}
        {loading ? (
          <div className="bg-[var(--card)] rounded-2xl border border-[var(--border)] p-12 text-center text-[var(--muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-[var(--card)] rounded-2xl border border-[var(--border)] p-12 text-center">
            <div className="text-4xl mb-2">🗂️</div>
            <div className="font-bold text-[var(--text)]">No leads yet</div>
            <div className="text-sm text-[var(--muted)] mt-1">Leads captured from your card &amp; microsite appear here.</div>
          </div>
        ) : view === 'grid' ? (
          <LeadGrid leads={filtered} onOpen={(id) => setCaptureLead(leads.find((l) => l.id === id) || null)} onWhatsApp={wa} />
        ) : (
          <LeadLedger leads={filtered} onOpen={(id) => setCaptureLead(leads.find((l) => l.id === id) || null)} onWhatsApp={wa} />
        )}
        </>
      </div>

      {/* Manual add-inquiry modal — creates a lead owned by this rep, then reloads. */}
      {showAdd && (
        <LeadQuickAdd onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />
      )}

      {/* Quick-enrich popup: record what the customer discussed. "View full details" opens the 360° view. */}
      {captureLead && (
        <LeadCaptureModal
          lead={captureLead}
          onClose={() => setCaptureLead(null)}
          onSaved={load}
          onView={(id) => { setCaptureLead(null); setOpenId(id); }}
        />
      )}
      {openId && <LeadDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
      <ChatPanel />
    </div>
  );
}
